import { Villa, STATUS_LABELS, STATUS_COLORS, STATUSES_ORDER } from '../types';
import { Card, CardContent } from './ui/card';
import { MoreHorizontalIcon, Link as LinkIcon, Archive, Star } from 'lucide-react';
import { Button } from './ui/button';
import { updateVilla } from '../api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuGroup,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuPortal,
} from './ui/dropdown-menu';

interface VillaCardProps {
  villa: Villa;
  viewMode: 'grid' | 'list';
  onSelect: (villa: Villa) => void;
  onUpdate: (villa: Villa) => void;
  onMoveToArchive: (id: string) => void;
}

export default function VillaCard({
  villa,
  viewMode,
  onSelect,
  onUpdate,
  onMoveToArchive,
}: VillaCardProps) {
  const handleTogglePin = async () => {
    try {
      const updated = await updateVilla(villa.id, { pinned: !villa.pinned });
      onUpdate(updated);
    } catch (err) {
      console.error('Failed to toggle pin:', err);
    }
  };

  const handleOpenLink = () => {
    window.open(villa.url, '_blank', 'noopener,noreferrer');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(villa.url);
    } catch {
      // ignore clipboard errors silently
    }
  };

  const handleStatusChange = async (status: Villa['status']) => {
    try {
      const updated = await updateVilla(villa.id, { status });
      onUpdate(updated);
    } catch (err) {
      console.error('Failed to save status:', err);
    }
  };

  return (
    <Card
      className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow relative"
      onClick={() => onSelect(villa)}
    >
      <CardContent className="p-0">
        {/* Image */}
        {villa.image ? (
          <div
            className={
              viewMode === 'grid'
                ? 'aspect-square w-full relative'
                : 'aspect-video w-full relative'
            }
          >
            {villa.status !== 'new' && (
              <div className="absolute left-2 top-2 rounded-full bg-black/70 px-2 py-1 text-white text-[11px] font-medium flex items-center gap-1">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: STATUS_COLORS[villa.status] }}
                />
                {STATUS_LABELS[villa.status]}
              </div>
            )}
            {villa.pinned && (
              <div className="absolute left-2 bottom-2 rounded-full bg-amber-500/90 p-1.5">
                <Star className="h-3 w-3 text-white fill-white" />
              </div>
            )}
            <img
              src={villa.image}
              alt={villa.title || ''}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div
            className={
              viewMode === 'grid'
                ? 'aspect-square w-full bg-muted flex items-center justify-center relative'
                : 'aspect-video w-full bg-muted flex items-center justify-center relative'
            }
          >
            {villa.status !== 'new' && (
              <div className="absolute left-2 top-2 rounded-full bg-black/70 px-2 py-1 text-white text-[11px] font-medium flex items-center gap-1">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: STATUS_COLORS[villa.status] }}
                />
                {STATUS_LABELS[villa.status]}
              </div>
            )}
            {villa.pinned && (
              <div className="absolute left-2 bottom-2 rounded-full bg-amber-500/90 p-1.5">
                <Star className="h-3 w-3 text-white fill-white" />
              </div>
            )}
            <span className="text-4xl">🏠</span>
          </div>
        )}

        {/* Content */}
        <div className="p-3">
          {/* Title */}
          <h3 className="font-medium text-sm line-clamp-2 mb-1">
            {villa.title || villa.domain}
          </h3>

          {/* Price */}
          {villa.priceFormatted && (
            <p className="text-sm font-semibold text-green-600 dark:text-green-400">
              {villa.priceFormatted}
            </p>
          )}

          {/* District or Domain */}
          <p className="text-xs text-muted-foreground mt-1">
            {villa.district || villa.domain}
          </p>
        </div>

        {/* Menu Button */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-7 w-7 text-white/70 hover:text-white hover:bg-black/40"
              onClick={(e) => e.stopPropagation()}
              aria-label="Меню"
            >
              <MoreHorizontalIcon className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuGroup>
              <DropdownMenuItem onSelect={() => onSelect(villa)}>
                Изменить телефон / цену
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  Статус: {STATUS_LABELS[villa.status]}
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="w-40">
                    {STATUSES_ORDER.map((status) => (
                      <DropdownMenuItem
                        key={status}
                        onSelect={() => handleStatusChange(status)}
                        className="flex items-center gap-2"
                      >
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: STATUS_COLORS[status] }}
                        />
                        {STATUS_LABELS[status]}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={handleOpenLink}>
                <LinkIcon className="h-4 w-4 mr-2" />
                Открыть ссылку
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={handleCopyLink}>
                <LinkIcon className="h-4 w-4 mr-2 rotate-45" />
                Скопировать ссылку
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={handleTogglePin}>
                {villa.pinned ? (
                  <>
                    <Star className="h-4 w-4 mr-2" />
                    Снять отметку
                  </>
                ) : (
                  <>
                    <Star className="h-4 w-4 mr-2" />
                    Пометить прикольным
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onMoveToArchive(villa.id)}>
                <Archive className="h-4 w-4 mr-2" />
                В архив
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardContent>
    </Card>
  );
}
